import type { AdminSport, AdminSportsbook, SportsbookOffer } from '../admin/api'
import { useEffect, useState } from 'react'
import {
  fetchSportAffiliateLinks,
  fetchSportsbookCarried,
  fetchSportsbookOffers,
  updateSportsbookGeneralUrl,
  updateSportsbookRegulatory,
} from '../admin/api'
import SportsbookBrandPanel from '../admin/SportsbookBrandPanel'
import StateGrid from '../admin/StateGrid'
import StringList from '../admin/StringList'
import { assetUrl } from '../api'

/**
 * /affiliate operator page.
 *
 * Sections: BRAND IDENTITY (shared SportsbookBrandPanel — color / logo
 * upload / fill, same panel the Distribution editor shows trading+) → REGULATORY
 * (editable — the book-level full statement that offer cards fall back to
 * when the offer has no override; migration 0047) → availability mirror →
 * per-sport affiliate links (the inverse of the sport view) → offers
 * referencing this operator → tracking/subId placeholder (data model
 * pending — the per-(operator, partner) URL param templates the offers
 * spec left open).
 */

interface Props {
  book: AdminSportsbook
  sports: AdminSport[]
  // Re-fetch the catalogs after a save so sport pages' offer previews pick
  // up the new book default.
  onSaved: () => void
}

type Mode = 'view' | 'edit'

interface SportLink {
  sportId: number
  sportName: string
  url: string | null
}

export default function AffiliateOperatorPage({ book, sports, onSaved }: Props) {
  const [sportLinks, setSportLinks] = useState<SportLink[] | null>(null)
  const [offers, setOffers] = useState<SportsbookOffer[] | null>(null)


  // Regulatory edit state.
  const [regMode, setRegMode] = useState<Mode>('view')
  const [regText, setRegText] = useState(book.regulatory_full ?? '')
  const [regBusy, setRegBusy] = useState(false)
  const [regMsg, setRegMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  useEffect(() => {
    setRegText(book.regulatory_full ?? '')
    setRegMode('view')
    setRegMsg(null)
  }, [book.id, book.regulatory_full])

  function cancelRegulatory() {
    setRegText(book.regulatory_full ?? '')
    setRegMode('view')
    setRegMsg(null)
  }

  async function saveRegulatory() {
    setRegBusy(true)
    setRegMsg(null)
    try {
      await updateSportsbookRegulatory(book.id, regText.trim())
      setRegText(regText.trim())
      setRegMode('view')
      setRegMsg({ kind: 'ok', text: 'regulatory saved' })
      onSaved()
    }
    catch (e) {
      setRegMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setRegBusy(false)
    }
  }

  // General link edit state — same view/edit pattern as REGULATORY.
  const [genMode, setGenMode] = useState<Mode>('view')
  const [genUrl, setGenUrl] = useState(book.general_url ?? '')
  const [genBusy, setGenBusy] = useState(false)
  const [genMsg, setGenMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  useEffect(() => {
    setGenUrl(book.general_url ?? '')
    setGenMode('view')
    setGenMsg(null)
  }, [book.id, book.general_url])

  function cancelGeneralUrl() {
    setGenUrl(book.general_url ?? '')
    setGenMode('view')
    setGenMsg(null)
  }

  async function saveGeneralUrl() {
    setGenBusy(true)
    setGenMsg(null)
    try {
      await updateSportsbookGeneralUrl(book.id, genUrl.trim())
      setGenUrl(genUrl.trim())
      setGenMode('view')
      setGenMsg({ kind: 'ok', text: 'general link saved' })
      onSaved()
    }
    catch (e) {
      setGenMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setGenBusy(false)
    }
  }

  useEffect(() => {
    setSportLinks(null)
    let cancelled = false
    ;(async () => {
      try {
        const carried = await fetchSportsbookCarried(book.id)
        // Dedupe sport ids — division rows repeat the sport.
        const ids = [...new Set(carried.map(c => c.sport_id))]
        // One links call per carried sport, filtered to this operator. N is
        // small (a book carries at most a few dozen sports) and the page is
        // owner-only — fine until a links-by-sportsbook endpoint exists.
        const rows = await Promise.all(ids.map(async (sportId) => {
          const links = await fetchSportAffiliateLinks(sportId).catch(() => [])
          const url = links.find(l => l.sportsbook_id === book.id)?.url ?? null
          const sport = sports.find(s => s.id === sportId)
          return { sportId, sportName: sport?.name ?? `#${sportId}`, url }
        }))
        if (!cancelled)
          setSportLinks(rows.sort((a, b) => a.sportName.localeCompare(b.sportName)))
      }
      catch {
        if (!cancelled)
          setSportLinks([])
      }
    })()
    return () => { cancelled = true }
  }, [book.id, sports])

  // Offers referencing this operator across all sports — real query by
  // sportsbook_id now that offers persist (migration 0044).
  useEffect(() => {
    setOffers(null)
    fetchSportsbookOffers(book.id)
      .then(setOffers)
      .catch(() => setOffers([]))
  }, [book.id])

  return (
    <section className="ad-editor ad-editor--full is-read-only">
      <style>{OPERATOR_PAGE_CSS}</style>
      <header className="ad-editor-head">
        <div className="ad-editor-head-stack">
          {(book.brand_color || book.logo_url) && (
            <div
              className={`ad-editor-logo${book.logo_fill ? ' is-fill' : ''}`}
              style={{ background: book.brand_color ?? 'transparent' }}
            >
              {book.logo_url
                ? <img src={assetUrl(book.logo_url)} alt="" loading="lazy" />
                : <span className="ad-editor-logo-text">{book.name.slice(0, 2).toUpperCase()}</span>}
            </div>
          )}
          <h2 className="b-h2">{book.name}</h2>
        </div>
      </header>

      <SportsbookBrandPanel book={book} canEdit onSaved={onSaved} />

      <div className="ad-editor-section">
        <h3 className="b-mono">STATUS</h3>
        <p className="b-mono afo-status">{book.status ?? 'active'}</p>
      </div>

      <div className="ad-editor-section">
        <div className="afx-section-head">
          <h3 className="b-mono">REGULATORY</h3>
          {regMode === 'view'
            ? (
                <button type="button" className="b-btn b-btn-ghost afx-section-btn" onClick={() => { setRegMode('edit'); setRegMsg(null) }}>
                  EDIT →
                </button>
              )
            : (
                <span className="afx-section-actions">
                  <button type="button" className="b-btn b-btn-primary afx-section-btn" disabled={regBusy} onClick={saveRegulatory}>
                    {regBusy ? 'SAVING…' : 'SAVE'}
                  </button>
                  <button type="button" className="b-btn b-btn-ghost afx-section-btn" disabled={regBusy} onClick={cancelRegulatory}>
                    CANCEL
                  </button>
                </span>
              )}
        </div>
        <p className="b-mono-dim ad-section-hint">
          Full regulatory statement for this book — every offer card falls
          back to it behind the expand caret unless the offer carries its own
          override. Plain text; line breaks preserved.
        </p>
        {regMsg && (
          <p className={`b-mono afx-msg${regMsg.kind === 'err' ? ' is-err' : ''}`}>
            {regMsg.kind === 'err' ? '⚠ ' : '✓ '}
            {regMsg.text}
          </p>
        )}
        {regMode === 'view'
          ? (
              regText.trim()
                ? <div className="afo-reg-text">{regText}</div>
                : (
                    <p className="b-mono-dim ad-list-empty">
                      not set — this book&apos;s offers show no expandable
                      statement unless they carry their own
                    </p>
                  )
            )
          : (
              <textarea
                className="ad-input afo-reg-input"
                rows={6}
                value={regText}
                onChange={e => setRegText(e.target.value)}
                placeholder="Must be 21+ and physically present in…"
                spellCheck={false}
              />
            )}
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">AVAILABILITY</h3>
        <p className="b-mono-dim ad-section-hint">
          Read-only mirror of the Distribution approvals — where this operator
          is live. Edit in Distribution &gt; Sportsbooks.
        </p>
        <StateGrid
          selected={[...book.sportsbook_states.map(s => s.state_code)].sort()}
          onChange={() => {}}
          readOnly
        />
        <div className="afo-intl">
          <StringList
            label="ACTIVE INTERNATIONAL"
            values={book.sportsbook_international.filter(r => r.kind === 'allowed').map(r => r.region).sort()}
            onChange={() => {}}
            readOnly
          />
          {book.sportsbook_international.some(r => r.kind === 'blocked') && (
            <StringList
              label="BLOCKED REGIONS"
              values={book.sportsbook_international.filter(r => r.kind === 'blocked').map(r => r.region).sort()}
              onChange={() => {}}
              readOnly
            />
          )}
        </div>
      </div>

      <div className="ad-editor-section">
        <div className="afx-section-head">
          <h3 className="b-mono">GENERAL LINK</h3>
          {genMode === 'view'
            ? (
                <button type="button" className="b-btn b-btn-ghost afx-section-btn" onClick={() => { setGenMode('edit'); setGenMsg(null) }}>
                  EDIT →
                </button>
              )
            : (
                <span className="afx-section-actions">
                  <button type="button" className="b-btn b-btn-primary afx-section-btn" disabled={genBusy} onClick={saveGeneralUrl}>
                    {genBusy ? 'SAVING…' : 'SAVE'}
                  </button>
                  <button type="button" className="b-btn b-btn-ghost afx-section-btn" disabled={genBusy} onClick={cancelGeneralUrl}>
                    CANCEL
                  </button>
                </span>
              )}
        </div>
        <p className="b-mono-dim ad-section-hint">
          Operator-level affiliate link — the GENERAL distribution grid and
          general availability map open this URL from the book&apos;s tile.
          Blank = tile renders unlinked. Per-sport embeds keep using the
          sport&apos;s own affiliate link below.
        </p>
        {genMsg && (
          <p className={`b-mono afx-msg${genMsg.kind === 'err' ? ' is-err' : ''}`}>
            {genMsg.kind === 'err' ? '⚠ ' : '✓ '}
            {genMsg.text}
          </p>
        )}
        {genMode === 'view'
          ? (
              genUrl.trim()
                ? (
                    <a className="afx-link-url" href={genUrl} target="_blank" rel="noopener noreferrer">
                      {genUrl}
                    </a>
                  )
                : (
                    <p className="b-mono-dim ad-list-empty">
                      not set — this book&apos;s tile on the general widgets
                      renders unlinked
                    </p>
                  )
            )
          : (
              <input
                className="ad-input afo-gen-input"
                type="url"
                value={genUrl}
                onChange={e => setGenUrl(e.target.value)}
                placeholder="https://…"
                spellCheck={false}
              />
            )}
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">AFFILIATE LINKS BY SPORT</h3>
        <p className="b-mono-dim ad-section-hint">
          Every sport this operator carries, with its affiliate URL. Read-only
          — edit per sport in Distribution.
        </p>
        {sportLinks === null
          ? <p className="b-mono-dim">LOADING…</p>
          : sportLinks.length === 0
            ? <p className="b-mono-dim ad-list-empty">this operator carries no sports on record</p>
            : (
                <div className="afx-links">
                  {sportLinks.map(row => (
                    <div key={row.sportId} className="afx-link-row">
                      <span className="afx-link-label b-mono">{row.sportName}</span>
                      {row.url
                        ? (
                            <a className="afx-link-url" href={row.url} target="_blank" rel="noopener noreferrer">
                              {row.url}
                            </a>
                          )
                        : <span className="b-mono-dim afx-link-none">no URL set</span>}
                    </div>
                  ))}
                </div>
              )}
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">OFFERS REFERENCING THIS OPERATOR</h3>
        <p className="b-mono-dim ad-section-hint">
          Every persisted offer whose tracking identity is this sportsbook,
          across all sports. Edit them on the sport pages.
        </p>
        {offers === null
          ? <p className="b-mono-dim">LOADING…</p>
          : offers.length === 0
            ? <p className="b-mono-dim ad-list-empty">no offers reference this operator</p>
            : (
                <div className="afx-links">
                  {offers.map(o => (
                    <div key={o.id} className="afo-offer-row">
                      <span className="b-mono-dim afo-offer-meta">
                        {(o.offer_containers?.sports?.slug ?? '?').toUpperCase()}
                        {' · '}
                        {(o.offer_containers?.name ?? '').replace(/ Offer Widget$/i, '')}
                      </span>
                      <span className="afo-offer-headline">{o.headline}</span>
                      {o.status !== 'published' && <span className="ofx-row-tag">DRAFT</span>}
                    </div>
                  ))}
                </div>
              )}
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">TRACKING / SUBID</h3>
        <div className="afo-placeholder b-mono-dim">
          Placeholder — per-(operator, partner) URL parameter templates. The
          offers handoff left this underspecified (where templates live, who
          supplies them); the planned shape is a Supabase table mapping
          (sportsbook, partner) → param template. Nothing to show until that
          lands.
        </div>
      </div>
    </section>
  )
}

const OPERATOR_PAGE_CSS = `
.afx-section-head { display: flex; align-items: center; gap: 16px; }
.afx-section-head h3 { margin: 0; }
.afx-section-actions { display: inline-flex; gap: 8px; }
.afx-section-btn { font-size: 11px; padding: 3px 10px; cursor: pointer; }
.afx-msg { font-size: 12px; margin: 6px 0 0; }
.afx-msg.is-err { color: #b91c1c; }
.brutalist-root.is-dark .afx-msg.is-err { color: #f87171; }
.afo-reg-text {
  font-size: 12px;
  line-height: 18px;
  white-space: pre-line;
  max-width: 640px;
}
.afo-reg-input {
  width: 100%;
  max-width: 640px;
  font-family: inherit;
  font-size: 12px;
  resize: vertical;
}
.afo-gen-input {
  width: 100%;
  max-width: 640px;
  font-family: inherit;
  font-size: 12px;
}
.afo-intl { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.afo-brand { display: flex; flex-direction: column; gap: 6px; }
.afo-brand-row {
  display: grid;
  grid-template-columns: minmax(120px, 160px) 1fr;
  gap: 12px;
  align-items: center;
  font-size: 12px;
}
.afo-brand-label { opacity: 0.7; }
.afo-status { font-size: 12px; margin: 0; }
.afo-brand-value { display: inline-flex; align-items: center; gap: 8px; word-break: break-all; }
.afo-swatch {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid rgba(128, 128, 128, 0.4);
  flex: 0 0 18px;
}
.afo-offer-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-size: 12px;
  flex-wrap: wrap;
}
.afo-offer-meta { font-size: 10px; white-space: nowrap; }
.afo-offer-headline { min-width: 0; }
.afo-placeholder {
  font-size: 12px;
  line-height: 18px;
  border: 1px dashed currentColor;
  border-radius: 6px;
  padding: 10px 12px;
  max-width: 640px;
}
.ofx-row-tag {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  background: #7f1d1d;
  color: #fff;
  border-radius: 3px;
}
.afx-links { display: flex; flex-direction: column; gap: 6px; }
.afx-link-row {
  display: grid;
  grid-template-columns: minmax(180px, 220px) 1fr;
  gap: 12px;
  align-items: center;
}
.afx-link-label { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; }
.afx-link-url { font-size: 12px; word-break: break-all; color: inherit; }
.afx-link-none { font-size: 12px; }
@media (max-width: 700px) {
  .afx-link-row { grid-template-columns: 1fr; gap: 2px; }
}
`
